package fyi.moko.hotpl.ui.friends

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Share
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fyi.moko.hotpl.domain.model.Friend
import fyi.moko.hotpl.domain.model.FriendSharedPlace
import fyi.moko.hotpl.domain.service.MutualWish
import fyi.moko.hotpl.domain.service.UsageEvents
import fyi.moko.hotpl.domain.service.UsageTracker
import fyi.moko.hotpl.ui.share.buildSharePlaceText
import fyi.moko.hotpl.ui.share.rememberShareTextLauncher
import fyi.moko.hotpl.ui.theme.mossShadow
import kotlinx.coroutines.launch
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

/**
 * Friends tab (Phase 3 Social MVP): my invite code, add-friend-by-code, and the friends
 * list with per-friend shared (PUBLIC/FRIENDS) places expanded inline. Online-only —
 * failures surface as snackbars, not crashes. Styled per the Stitch "friends_compact"
 * mockup (docs/design-ref) — invite code and add-friend are always-visible cards at the
 * top of the list rather than behind an "Add friend" sheet.
 */
@Composable
fun FriendsScreen(
    modifier: Modifier = Modifier,
) {
    val viewModel: FriendsViewModel = koinViewModel()
    val myInviteCode by viewModel.myInviteCode.collectAsStateWithLifecycle()
    val inviteCodeLoading by viewModel.inviteCodeLoading.collectAsStateWithLifecycle()
    val friends by viewModel.friends.collectAsStateWithLifecycle()
    val redeemInput by viewModel.redeemInput.collectAsStateWithLifecycle()
    val redeemInProgress by viewModel.redeemInProgress.collectAsStateWithLifecycle()
    val expandedFriendUid by viewModel.expandedFriendUid.collectAsStateWithLifecycle()
    val sharedPlaces by viewModel.sharedPlaces.collectAsStateWithLifecycle()
    val sharedPlacesLoading by viewModel.sharedPlacesLoading.collectAsStateWithLifecycle()
    val addToJournalInProgress by viewModel.addToJournalInProgress.collectAsStateWithLifecycle()
    val previewPlace by viewModel.previewPlace.collectAsStateWithLifecycle()
    // Collected so the preview's "In your journal" reflects the live journal.
    val journalKeys by viewModel.journalKeys.collectAsStateWithLifecycle()
    val mutualWishes by viewModel.mutualWishes.collectAsStateWithLifecycle()
    val mutualWishesReady by viewModel.mutualWishesReady.collectAsStateWithLifecycle()
    val message by viewModel.uiMessage.collectAsStateWithLifecycle()

    val snackbarHostState = remember { SnackbarHostState() }
    val scope = rememberCoroutineScope()
    val shareText = rememberShareTextLauncher()
    val usageTracker = koinInject<UsageTracker>()
    var friendPendingRemoval by remember { mutableStateOf<Friend?>(null) }

    LaunchedEffect(message) {
        message?.let { msg ->
            scope.launch { snackbarHostState.showSnackbar(msg) }
            viewModel.clearUiMessage()
        }
    }

    // Box so the SnackbarHost can overlay the content — this screen lives inside
    // HomeScreen's tab content, not its own Scaffold (same reasoning as DirectoryScreen).
    Box(modifier = modifier.fillMaxSize()) {
        val selectedFriend = friends.firstOrNull { it.uid == expandedFriendUid }
        val preview = previewPlace
        if (selectedFriend != null && preview != null) {
            FriendPlacePreview(
                friend = selectedFriend,
                place = preview,
                inJournal = journalKeys.isNotEmpty() && viewModel.isInJournal(preview),
                addInProgress = preview.placeId in addToJournalInProgress,
                onBack = viewModel::closePreview,
                onAddToJournal = { viewModel.addToJournal(preview) },
            )
        } else if (selectedFriend == null) {
            FriendsList(
                code = myInviteCode?.code,
                codeLoading = inviteCodeLoading,
                redeemInput = redeemInput,
                redeemInProgress = redeemInProgress,
                friends = friends,
                mutualWishes = mutualWishes,
                mutualWishesReady = mutualWishesReady,
                onRedeemInputChange = viewModel::onRedeemInputChange,
                onRedeem = viewModel::redeemCode,
                onShare = { code -> shareText("Add me on HOTPL — invite code: $code") },
                onOpenWish = viewModel::openMutualWish,
                onShareWish = { wish ->
                    usageTracker.track(UsageEvents.FRIENDS_ACTION, mapOf("kind" to "mutual_share"))
                    shareText(buildMutualWishShareText(wish))
                },
                onRegenerate = viewModel::regenerateInviteCode,
                onFriendClick = viewModel::onFriendClick,
                onRemoveClick = { friendPendingRemoval = it },
            )
        } else {
            FriendDetail(
                friend = selectedFriend,
                places = sharedPlaces[selectedFriend.uid],
                loadingPlaces = selectedFriend.uid in sharedPlacesLoading,
                onBack = { viewModel.onFriendClick(selectedFriend) },
                onRemove = { friendPendingRemoval = selectedFriend },
                onOpenPlace = viewModel::openPreview,
            )
        }

        SnackbarHost(
            hostState = snackbarHostState,
            modifier = Modifier.align(Alignment.BottomCenter),
        )
    }

    friendPendingRemoval?.let { friend ->
        AlertDialog(
            onDismissRequest = { friendPendingRemoval = null },
            title = { Text("Remove friend") },
            text = { Text("Remove ${friend.displayName}? You'll both stop seeing each other's shared places.") },
            confirmButton = {
                TextButton(onClick = {
                    viewModel.removeFriend(friend)
                    friendPendingRemoval = null
                }) { Text("Remove") }
            },
            dismissButton = {
                TextButton(onClick = { friendPendingRemoval = null }) { Text("Cancel") }
            },
        )
    }
}

@Composable
private fun FriendsList(
    code: String?,
    codeLoading: Boolean,
    redeemInput: String,
    redeemInProgress: Boolean,
    friends: List<Friend>,
    mutualWishes: List<MutualWish>,
    mutualWishesReady: Boolean,
    onRedeemInputChange: (String) -> Unit,
    onRedeem: () -> Unit,
    onShare: (String) -> Unit,
    onOpenWish: (MutualWish) -> Unit,
    onShareWish: (MutualWish) -> Unit,
    onRegenerate: () -> Unit,
    onFriendClick: (Friend) -> Unit,
    onRemoveClick: (Friend) -> Unit,
) {
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(horizontal = 20.dp, vertical = 16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        item(key = "friends-title") {
            Text("Friends", style = MaterialTheme.typography.headlineMedium)
        }
        item(key = "invite-code-card") {
            InviteCodeCard(code = code, loading = codeLoading, onShare = onShare, onRegenerate = onRegenerate)
        }
        item(key = "add-friend-card") {
            AddFriendCard(
                redeemInput = redeemInput,
                redeemInProgress = redeemInProgress,
                onRedeemInputChange = onRedeemInputChange,
                onRedeem = onRedeem,
            )
        }
        if (friends.isEmpty()) {
            item(key = "empty") {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(vertical = 32.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Text("No friends yet", style = MaterialTheme.typography.titleMedium)
                    Spacer(Modifier.height(4.dp))
                    Text(
                        "Share your code above, or enter someone else's to connect.",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        textAlign = TextAlign.Center,
                    )
                }
            }
        } else {
            item(key = "mutual-wishes-label") {
                Text(
                    "You both want to go",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(top = 8.dp),
                )
            }
            if (mutualWishes.isEmpty()) {
                item(key = "mutual-wishes-empty") {
                    Text(
                        if (mutualWishesReady) {
                            "Nothing yet — a place shows up here once you and a friend both have it as Want to go and they share it with Friends."
                        } else {
                            "Checking your friends' lists…"
                        },
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            } else {
                items(mutualWishes, key = { "wish-" + it.place.ownerId + "/" + it.place.placeId }) { wish ->
                    MutualWishCard(wish = wish, onClick = { onOpenWish(wish) }, onShare = { onShareWish(wish) })
                }
            }
            item(key = "friends-count") {
                Text(
                    "Your Friends (${friends.size})",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(top = 8.dp),
                )
            }
            itemsIndexed(friends, key = { _, friend -> friend.friendshipId }) { index, friend ->
                FriendListRow(
                    friend = friend,
                    avatarColorAlternate = index % 2 == 1,
                    onClick = { onFriendClick(friend) },
                    onRemoveClick = { onRemoveClick(friend) },
                )
            }
        }
    }
}

/**
 * One place a friend and I both want to go to (0-37 stage 1). Tapping opens the friend's
 * preview (which says "In your journal" — the place is already mine); the share button is
 * the OS share sheet, because arranging the outing happens in Messages/KakaoTalk, not here,
 * so there is deliberately no in-app "let's go".
 */
@Composable
private fun MutualWishCard(
    wish: MutualWish,
    onClick: () -> Unit,
    onShare: () -> Unit,
) {
    val place = wish.place
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(MaterialTheme.colorScheme.surfaceContainerLowest)
            .clickable(onClick = onClick)
            .padding(start = 12.dp, top = 12.dp, bottom = 12.dp, end = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        SharedCoverImage(
            ownerUid = place.ownerId,
            fileName = place.photos.firstOrNull(),
            tags = place.tags,
            contentDescription = null,
            modifier = Modifier.size(56.dp).clip(RoundedCornerShape(10.dp)),
        )
        Column(modifier = Modifier.weight(1f)) {
            Text(place.name, style = MaterialTheme.typography.titleMedium)
            Text(
                mutualWishCaption(wish.friends.map { it.displayName }),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        IconButton(onClick = onShare) {
            Icon(Icons.Default.Share, contentDescription = "Share ${place.name}", tint = MaterialTheme.colorScheme.tertiary)
        }
    }
}

/** The message behind the card's share button: a nudge plus the usual place line + map link. */
internal fun buildMutualWishShareText(wish: MutualWish): String =
    "Want to go together?\n" + buildSharePlaceText(
        name = wish.place.name,
        locationContext = null,
        latitude = wish.place.latitude,
        longitude = wish.place.longitude,
    )

@Composable
private fun FriendListRow(
    friend: Friend,
    avatarColorAlternate: Boolean,
    onClick: () -> Unit,
    onRemoveClick: () -> Unit,
) {
    val avatarBg = if (avatarColorAlternate) {
        MaterialTheme.colorScheme.tertiaryContainer
    } else {
        MaterialTheme.colorScheme.secondaryContainer
    }
    val avatarFg = if (avatarColorAlternate) {
        MaterialTheme.colorScheme.onTertiaryContainer
    } else {
        MaterialTheme.colorScheme.onSecondaryContainer
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(MaterialTheme.colorScheme.surfaceContainerLowest)
            .clickable(onClick = onClick)
            .padding(16.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        Box(
            modifier = Modifier.size(48.dp).clip(CircleShape).background(avatarBg),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                friend.displayName.take(1).uppercase(),
                style = MaterialTheme.typography.headlineSmall,
                color = avatarFg,
            )
        }
        Text(friend.displayName, style = MaterialTheme.typography.titleLarge, modifier = Modifier.weight(1f))
        IconButton(onClick = onRemoveClick) {
            Icon(
                Icons.Default.MoreVert,
                contentDescription = "Remove ${friend.displayName}",
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        Icon(
            Icons.AutoMirrored.Filled.KeyboardArrowRight,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun AddFriendCard(
    redeemInput: String,
    redeemInProgress: Boolean,
    onRedeemInputChange: (String) -> Unit,
    onRedeem: () -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceContainerLowest),
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(
                "ADD A FRIEND",
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                OutlinedTextField(
                    value = redeemInput,
                    onValueChange = onRedeemInputChange,
                    modifier = Modifier.weight(1f),
                    placeholder = { Text("Enter their code") },
                    textStyle = MaterialTheme.typography.bodyMedium.copy(fontFamily = FontFamily.Monospace),
                    singleLine = true,
                )
                Button(
                    onClick = onRedeem,
                    enabled = redeemInput.isNotBlank() && !redeemInProgress,
                    shape = RoundedCornerShape(8.dp),
                ) {
                    if (redeemInProgress) {
                        CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                    } else {
                        Text("Add")
                    }
                }
            }
        }
    }
}

/**
 * Shown instead of [FriendsScreen] for an anonymous session (appbar design doc,
 * 2026-07-19 — "Directory / Friends 탭 노출 게이팅"). Friendships are keyed to the Firebase
 * uid; an anonymous uid is lost on reinstall, so linking is a prerequisite rather than a
 * nice-to-have. Shown (not hidden) so the feature stays discoverable.
 */
@Composable
fun FriendsLockedPanel(
    onOpenAccount: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxSize().padding(24.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        Icon(
            Icons.Default.Lock,
            contentDescription = null,
            modifier = Modifier.size(40.dp),
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Spacer(Modifier.height(12.dp))
        Text(
            "Link your account to use Friends",
            style = MaterialTheme.typography.titleMedium,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(4.dp))
        Text(
            "Friendships are tied to your account — an unlinked session loses them on reinstall.",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(16.dp))
        Button(onClick = onOpenAccount) { Text("Go to Account") }
    }
}

@Composable
private fun InviteCodeCard(
    code: String?,
    loading: Boolean,
    onShare: (String) -> Unit,
    onRegenerate: () -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth().mossShadow(elevation = 6.dp, shape = RoundedCornerShape(12.dp)),
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceContainerLow),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                when {
                    loading -> CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
                    code != null -> Text(
                        code,
                        style = MaterialTheme.typography.bodyLarge,
                        fontFamily = FontFamily.Monospace,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.primary,
                    )
                    else -> Text(
                        "Unavailable offline",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                Spacer(Modifier.weight(1f))
                if (code != null && !loading) {
                    IconButton(onClick = onRegenerate) {
                        Icon(
                            Icons.Default.Refresh,
                            contentDescription = "Regenerate invite code",
                            tint = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                    IconButton(onClick = { onShare(code) }) {
                        Icon(Icons.Default.Share, contentDescription = "Share invite code", tint = MaterialTheme.colorScheme.tertiary)
                    }
                }
            }
            Text(
                "Instant friend code — no approval.",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun FriendDetail(
    friend: Friend,
    places: List<FriendSharedPlace>?,
    loadingPlaces: Boolean,
    onBack: () -> Unit,
    onRemove: () -> Unit,
    onOpenPlace: (FriendSharedPlace) -> Unit,
) {
    var showMenu by remember { mutableStateOf(false) }
    LazyColumn(modifier = Modifier.fillMaxSize().padding(horizontal = 16.dp)) {
        item(key = "friend-detail-title") {
            Row(
                modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                IconButton(onClick = onBack) {
                    Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back to friends")
                }
                Text(friend.displayName, style = MaterialTheme.typography.headlineSmall, modifier = Modifier.weight(1f))
                Box {
                    IconButton(onClick = { showMenu = true }) {
                        Icon(Icons.Default.MoreVert, contentDescription = "Friend options")
                    }
                    DropdownMenu(expanded = showMenu, onDismissRequest = { showMenu = false }) {
                        DropdownMenuItem(
                            text = { Text("Remove friend") },
                            onClick = {
                                showMenu = false
                                onRemove()
                            },
                        )
                    }
                }
            }
        }
        item(key = "shared-places-label") {
            Text(
                "SHARED PLACES",
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.secondary,
                modifier = Modifier.padding(top = 12.dp, bottom = 8.dp),
            )
        }
        when {
            loadingPlaces -> item(key = "loading") {
                Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
                }
            }
            places.isNullOrEmpty() -> item(key = "no-places") {
                Text(
                    "No shared places yet",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(vertical = 24.dp),
                )
            }
            else -> items(places, key = { it.placeId }) { place ->
                SharedPlaceRow(place = place, onClick = { onOpenPlace(place) })
                HorizontalDivider()
            }
        }
    }
}

/**
 * One shared place: thumbnail, name, the friend's status. Tapping opens the preview — there
 * is deliberately no add button here (0-36 d): you look first, then decide.
 */
@Composable
private fun SharedPlaceRow(
    place: FriendSharedPlace,
    onClick: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick).padding(vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        SharedCoverImage(
            ownerUid = place.ownerId,
            fileName = place.photos.firstOrNull(),
            tags = place.tags,
            contentDescription = null,
            modifier = Modifier.size(56.dp).clip(RoundedCornerShape(10.dp)),
        )
        Column(modifier = Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(place.name, style = MaterialTheme.typography.titleMedium)
                if (place.liked) {
                    Spacer(Modifier.width(4.dp))
                    // An icon, not the "♥" glyph: the web fonts have no heart and drew a box (0-38).
                    Icon(
                        Icons.Default.Favorite,
                        contentDescription = "Liked",
                        tint = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.size(16.dp),
                    )
                }
            }
            val details = buildList {
                add(place.status.name.lowercase().replaceFirstChar { it.uppercase() })
                addAll(place.tags.take(2))
                if (place.tags.size > 2) add("+${place.tags.size - 2}")
            }.joinToString(" · ")
            Text(details, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        Icon(
            Icons.AutoMirrored.Filled.KeyboardArrowRight,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}
